import { GRID_ACTIONS_CELL_ATTR } from './gridColumns';
import {
    findHorizontalScroller,
    startDragAutoScroll
} from './gridDragAutoScroll';

/**
 * Attribute on every cell of a column taking part in a drag (see
 * gridColumnDnd.styles.js): `lifted` — the dragged column, following the
 * pointer; `shifting` — a column making room for it; `settling` — the
 * dragged column gliding into its new place after the drop; `returning` —
 * columns gliding back when the drag is called off.
 * @type {string}
 */
export const GRID_COLUMN_DRAG_ATTR = 'data-col-drag';

// How far the pointer travels before a press on a head cell becomes a drag:
// below it the press is a click (sorting).
const START_DISTANCE = 5;
// Kept in step with the transitions in gridColumnDnd.styles.js.
export const GRID_COLUMN_SHIFT_MS = 180;
export const GRID_COLUMN_SETTLE_MS = 150;

const prefersReducedMotion = () =>
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// The body takes the grabbing cursor for the whole drag and text selection
// is off, so the drag never paints a selection across the table.
const lockBody = () => {
    const { style } = document.body;
    const previous = { cursor: style.cursor, userSelect: style.userSelect };
    style.cursor = 'grabbing';
    style.userSelect = 'none';
    window.getSelection()?.removeAllRanges();
    return () => {
        style.cursor = previous.cursor;
        style.userSelect = previous.userSelect;
    };
};

// Every cell of the column at `index`, head to totals; rows that do not line
// up with the head (no results) are left out.
const columnCells = (table, index, cellCount) =>
    Array.from(table.rows)
        .filter((row) => row.cells.length === cellCount)
        .map((row) => row.cells[index]);

const setCells = (cells, state, offset) => {
    cells.forEach((cell) => {
        if (state) cell.setAttribute(GRID_COLUMN_DRAG_ATTR, state);
        else cell.removeAttribute(GRID_COLUMN_DRAG_ATTR);
        cell.style.transform = offset ? `translateX(${offset}px)` : '';
    });
};

const clearCells = (cells) => {
    cells.forEach((cell) => {
        cell.removeAttribute(GRID_COLUMN_DRAG_ATTR);
        cell.style.transform = '';
        cell.style.transition = '';
    });
};

/**
 * Follows a press on a reorderable head cell. Once the pointer has moved a
 * few pixels the column is lifted: all its cells follow the pointer, the
 * other columns slide aside to show where it would land, and the table
 * scrolls at its edges. On release the grid commits the new order through
 * `onMove` and the column glides from where it was let go into its new
 * place; Escape (or a release where it started) brings everything back.
 *
 * Only data columns (head cells with `data-col-id`) take part: the dragged
 * column stays between the first and the last of them, so it never covers
 * the checkbox or the pinned actions column.
 *
 * @param {PointerEvent} event - The `pointerdown` on the head cell.
 * @param {Object} handlers - Callbacks.
 * @param {Function} handlers.onMove - `(fromId, toId, side)` with `side`
 *   'before' | 'after'; returns a promise settled once the grid has
 *   re-rendered in the new order.
 * @param {Function} [handlers.onEnd] - Called once the press is over,
 *   whether or not it became a drag.
 * @returns {Object} `{ cancel() }` to drop the drag at once (unmount)
 */
export function startColumnDrag(event, { onMove, onEnd }) {
    const headCell = event.currentTarget;
    const headRow = headCell.parentElement;
    const table = headCell.closest('table');
    const { pointerId } = event;
    const startX = event.clientX;
    const startY = event.clientY;
    let clientX = startX;
    let drag = null;

    const listen = (add) => {
        const method = add ? 'addEventListener' : 'removeEventListener';
        window[method]('pointermove', handleMove);
        window[method]('pointerup', handleUp);
        window[method]('pointercancel', handleCancel);
        window[method]('keydown', handleKey, true);
    };

    // Column geometry is taken once, in table coordinates, so scrolling
    // does not change it.
    function lift() {
        const cellCount = headRow.cells.length;
        const columns = Array.from(headRow.cells)
            .filter((cell) => cell.dataset.colId)
            .map((cell) => ({
                id: cell.dataset.colId,
                left: cell.offsetLeft,
                width: cell.offsetWidth,
                cells: columnCells(table, cell.cellIndex, cellCount),
                offset: 0
            }));
        const from = columns.findIndex((c) => c.id === headCell.dataset.colId);
        const scroller = findHorizontalScroller(table);
        const pinned = headRow.querySelector(`[${GRID_ACTIONS_CELL_ATTR}]`);
        const first = columns[0];
        const last = columns[columns.length - 1];

        const state = {
            columns,
            from,
            target: from,
            dx: 0,
            scroller,
            startScrollLeft: scroller?.scrollLeft ?? 0,
            minLeft: first.left,
            maxRight: last.left + last.width,
            autoScroll:
                scroller &&
                startDragAutoScroll(
                    scroller,
                    () => pinned?.getBoundingClientRect().left
                ),
            unlockBody: lockBody()
        };
        headCell.setPointerCapture?.(pointerId);
        columns.forEach((column, index) =>
            setCells(column.cells, index === from ? 'lifted' : 'shifting')
        );
        scroller?.addEventListener('scroll', layout);
        return state;
    }

    // Places the dragged column under the pointer and the others around it.
    function layout() {
        const { columns, from, scroller } = drag;
        const dragged = columns[from];
        const panned = scroller ? scroller.scrollLeft - drag.startScrollLeft : 0;
        drag.dx = Math.min(
            Math.max(clientX - startX + panned, drag.minLeft - dragged.left),
            drag.maxRight - dragged.left - dragged.width
        );
        setCells(dragged.cells, 'lifted', drag.dx);

        // It lands past every column whose middle it has crossed.
        const center = dragged.left + drag.dx + dragged.width / 2;
        let target = from;
        columns.forEach((column, index) => {
            const middle = column.left + column.width / 2;
            if (index < from && center < middle) target = Math.min(target, index);
            if (index > from && center > middle) target = Math.max(target, index);
        });
        drag.target = target;

        columns.forEach((column, index) => {
            if (index === from) return;
            let offset = 0;
            if (index > from && index <= target) offset = -dragged.width;
            if (index < from && index >= target) offset = dragged.width;
            if (offset !== column.offset) {
                column.offset = offset;
                setCells(column.cells, 'shifting', offset);
            }
        });
    }

    function release() {
        drag.autoScroll?.stop();
        drag.scroller?.removeEventListener('scroll', layout);
        drag.unlockBody();
        if (headCell.hasPointerCapture?.(pointerId)) {
            headCell.releasePointerCapture(pointerId);
        }
        // The click that follows the release is the end of the drag, not a
        // click on the head cell (which would sort).
        const swallow = (clickEvent) => {
            clickEvent.stopPropagation();
            clickEvent.preventDefault();
        };
        window.addEventListener('click', swallow, { capture: true, once: true });
        setTimeout(() => window.removeEventListener('click', swallow, true));
    }

    // Everything glides back to where it was.
    function goBack() {
        const cells = drag.columns.flatMap((column) => column.cells);
        if (prefersReducedMotion()) {
            clearCells(cells);
            return;
        }
        cells.forEach((cell) => cell.setAttribute(GRID_COLUMN_DRAG_ATTR, 'returning'));
        // eslint-disable-next-line no-unused-expressions
        table.offsetWidth; // start the transition from the current offsets
        cells.forEach((cell) => {
            cell.style.transform = '';
        });
        setTimeout(() => clearCells(cells), GRID_COLUMN_SHIFT_MS);
    }

    // The new order is in: the columns stand where they were shown, so only
    // the dragged one moves, from where it was let go into its place.
    function settle(dragged, droppedLeft) {
        clearCells(drag.columns.flatMap((column) => column.cells));
        const cell = headRow.querySelector(
            `th[data-col-id="${CSS.escape(dragged.id)}"]`
        );
        if (!cell || prefersReducedMotion()) return;
        const cells = columnCells(table, cell.cellIndex, headRow.cells.length);
        const offset = droppedLeft - cell.offsetLeft;
        if (!offset) return;
        setCells(cells, 'settling', offset);
        // eslint-disable-next-line no-unused-expressions
        table.offsetWidth;
        cells.forEach((c) => {
            c.style.transition = `transform ${GRID_COLUMN_SETTLE_MS}ms ease-out`;
            c.style.transform = '';
        });
        setTimeout(() => clearCells(cells), GRID_COLUMN_SETTLE_MS);
    }

    function finish(commit) {
        listen(false);
        if (drag) {
            release();
            const { columns, from, target } = drag;
            if (!commit || target === from) {
                goBack();
            } else {
                const dragged = columns[from];
                const droppedLeft = dragged.left + drag.dx;
                onMove(
                    dragged.id,
                    columns[target].id,
                    target > from ? 'after' : 'before'
                ).then(() => settle(dragged, droppedLeft));
            }
        }
        onEnd?.();
    }

    function handleMove(moveEvent) {
        if (moveEvent.pointerId !== pointerId) return;
        clientX = moveEvent.clientX;
        if (!drag) {
            const distance = Math.hypot(
                moveEvent.clientX - startX,
                moveEvent.clientY - startY
            );
            if (distance < START_DISTANCE) return;
            drag = lift();
        }
        drag.autoScroll?.update(clientX);
        layout();
    }

    function handleUp(upEvent) {
        if (upEvent.pointerId === pointerId) finish(true);
    }

    function handleCancel(cancelEvent) {
        if (cancelEvent.pointerId === pointerId) finish(false);
    }

    function handleKey(keyEvent) {
        if (keyEvent.key !== 'Escape' || !drag) return;
        keyEvent.preventDefault();
        keyEvent.stopPropagation();
        finish(false);
    }

    listen(true);

    return {
        cancel() {
            listen(false);
            if (drag) {
                release();
                clearCells(drag.columns.flatMap((column) => column.cells));
            }
            onEnd?.();
        }
    };
}
